import { useState } from "react";
import apiClient from "./apiClient";
import { API_ENDPOINTS } from "./endPoints";

// Types for HLS feedback and sharing
export interface ShareRequest {
  contentId: string;
  message: string;
  ttl: "1hour" | "1day" | "1week" | "1month" | "permanent";
  hlsUrlToCopy: string;
  recipientName?: string;
  recipientEmail?: string;
  sendEmail?: boolean;
}

export interface ShareResponse {
  id: string;
  url: string;
  expiresAt: string;
}

// New IssueCast publish types
export interface IssueCastChapter {
  title: string;
  startTime: number; // seconds offset
  runTimeSeconds: number; // duration of chapter in seconds
  linkToOriginal?: string | null;
  script?: any; // Script information for the chapter
}

export interface IssueCastParams {
  hlsUrl: string;
  publishDate: string; // ISO-8601
  totalRunTime: number; // seconds
  chapters: IssueCastChapter[];
  websiteId?: string;
  locationName: string;
  name?: string;
  description?: string;
  podcastType?: 'multiple_articles' | 'emergency';
  classification?: {
    incident_types?: string[];
    locations?: string[];
    severity_tags?: string[];
    timestamp?: string;
  };
}

export interface IssueCastPublishRequest {
  issueCastParams: IssueCastParams;
  // websiteId removed from top-level (backward compatibility shim handled in publishIssueCast)
}

export interface IssueCastPublisherMeta {
  name?: string;
  logo?: string;
  tagline?: string;
}

export interface IssueCastSponsorMeta {
  name?: string;
  sponsorMention?: string;
  logoURL?: string;
  clickThroughURL?: string;
}

export interface IssueCast {
  id: string;
  hlsUrl: string;
  publishDate: string;
  totalRunTime: number;
  publisher?: IssueCastPublisherMeta;
  sponsor?: IssueCastSponsorMeta;
  chapters: IssueCastChapter[];
  createdAt?: string;
  updatedAt?: string;
  name?: string;
  description?: string;
}

export interface IssueCastPublishResponse {
  success?: boolean;
  issueCast?: IssueCast;
  error?: string;
  fields?: string[];
}

// Given a URL, download the HLS stream as a WAV file
const downloadHlsAsWave = async (url: string): Promise<Blob> => {
  const response = await apiClient({
    method: 'get',
    url: API_ENDPOINTS.NOAUTH.DOWNLOAD_HLS_AS_WAV,
    params: { url },
    responseType: 'blob',
  });
  return response.data;
};

// Hook for HLS feedback and sharing functionality
export const useHLSFeedback = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shareResponse, setShareResponse] = useState<ShareResponse | null>(null);
  // New state for IssueCast publishing
  const [publishLoading, setPublishLoading] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [issueCastResponse, setIssueCastResponse] = useState<IssueCast | null>(null);

  // Send feedback (thumbs up/down)
  const sendFeedback = async (contentId: string, type: "up" | "down" | null) => {
    setIsLoading(true);
    setError(null);

    try {
      // In a real implementation, this would send a request to the API
      // For now, we'll just log the feedback and simulate success
      console.log(`Sending ${type} feedback for content ${contentId}`);

      /* 
      // Uncomment for real implementation
      await axios.post(`${API_BASE_URL}/content/feedback`, {
        contentId,
        type
      });
      */

      // Simulate API delay
      await new Promise((resolve) => setTimeout(resolve, 300));

      setIsLoading(false);
      return true;
    } catch (err) {
      console.error("Error sending feedback:", err);
      setError("Failed to send feedback");
      setIsLoading(false);
      return false;
    }
  };

  // Create share
  const createShare = async (shareRequest: ShareRequest) => {
    setIsLoading(true);
    setError(null);
    setShareResponse(null);

    try {
      const response = await apiClient.post(API_ENDPOINTS.PUBLISHER.PUBLISHERS.SHARE, {
        shareRequest,
      });

      const shareResponse: ShareResponse = {
        id: response.data.shareId,
        url: response.data.newHlsUrl,
        expiresAt: new Date(Date.now() + getExpiryMilliseconds(shareRequest.ttl)).toISOString(),
      };

      setShareResponse(shareResponse);
      setIsLoading(false);
      return shareResponse;
    } catch (err) {
      console.error("Error creating share:", err);
      setError("Failed to create share");
      setIsLoading(false);
      throw err;
    }
  };

  // Publish IssueCast
  const publishIssueCast = async (request: IssueCastPublishRequest): Promise<IssueCastPublishResponse> => {
    setPublishLoading(true);
    setPublishError(null);
    setIssueCastResponse(null);
    try {
      // Backward compatibility: if someone still passes a top-level websiteId, fold it in
      // (Type no longer exposes it, but defensive just in case)
      // @ts-ignore
      if ((request as any).websiteId && !request.issueCastParams.websiteId) {
        request = {
          issueCastParams: { ...request.issueCastParams, websiteId: (request as any).websiteId }
        };
      }
      const response = await apiClient.post(API_ENDPOINTS.PUBLISHER.ISSUECASTS.PUBLISH_ISSUECAST, request);
      const data: IssueCastPublishResponse = response.data;
      if (data.issueCast) {
        setIssueCastResponse(data.issueCast);
      }
      setPublishLoading(false);
      return data;
    } catch (e: any) {
      const msg = e?.response?.data?.error || 'Failed to publish IssueCast';
      setPublishError(msg);
      setPublishLoading(false);
      return { error: msg };
    }
  };

  // Helper to calculate expiry time based on TTL
  const getExpiryMilliseconds = (ttl: string): number => {
    switch (ttl) {
      case "1hour":
        return 60 * 60 * 1000;
      case "1day":
        return 24 * 60 * 60 * 1000;
      case "1week":
        return 7 * 24 * 60 * 60 * 1000;
      case "1month":
        return 30 * 24 * 60 * 60 * 1000;
      default:
        return 24 * 60 * 60 * 1000; // Default to 1 day
    }
  };

  return {
    isLoading,
    error,
    shareResponse,
    sendFeedback,
    createShare,
    downloadHlsAsWave,
    // IssueCast publish extras
    publishIssueCast,
    publishLoading,
    publishError,
    issueCastResponse,
  };
};

export { downloadHlsAsWave };
